/**
 * PATH: packages/ui/src/components/settings/aiUsageFormat.ts
 * FILE_ID: UI-SETTINGS-AIUSAGE-FORMAT-001
 * PURPOSE:
 *   Pure value-formatting helpers + window constants for the AI usage
 *   dashboard. Extracted verbatim from AiUsageDashboard.tsx so the
 *   component file stays under the 300-line rule and so these I→O
 *   functions are unit-testable in isolation (see aiUsageFormat.test.ts).
 * WHY:
 *   These run on every row render and on CSV export; pinning their
 *   exact output (tiered USD precision, locale-grouped integers,
 *   null-safe timestamps) prevents formatting regressions like the
 *   2026-05 blank-tile bug from creeping back in via a refactor.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────┐   ┌──────────────┐
 *   │ raw value    │ → │ format* (pure I→O) │ → │ display text │
 *   └──────────────┘   └───────────────────┘   └──────────────┘
 * DEPENDENCIES:
 *   - none (pure)
 * RELATED:
 *   - AiUsageDashboard.tsx: sole UI consumer
 *   - useAiUsageDashboard.ts: consumes PERIOD_OPTIONS / PAGE_SIZE
 */

export const PERIOD_OPTIONS: { label: string; days: number }[] = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '365d', days: 365 },
]

export const PAGE_SIZE = 50

/**
 * Tiered-precision USD formatter. AI line items range from fractions of
 * a cent (a single cached completion) to hundreds of dollars (a monthly
 * roll-up), so a fixed 2dp would render "$0.00" for most real rows.
 * Failure mode: non-numeric / null / undefined → "$0.00".
 */
export const formatUSD = (n: number | string | null | undefined): string => {
  const v = Number(n ?? 0)
  if (Number.isNaN(v)) return '$0.00'
  if (v >= 100) return `$${v.toFixed(2)}`
  if (v >= 1) return `$${v.toFixed(3)}`
  return `$${v.toFixed(4)}`
}

/**
 * Locale-grouped integer formatter (drops the fractional part).
 * Failure mode: non-numeric / null / undefined → "0".
 */
export const formatNumber = (n: number | string | null | undefined): string =>
  Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })

/**
 * Null-safe local timestamp. Failure mode: missing or unparseable ISO
 * string → "—" (em dash) rather than "Invalid Date".
 */
export const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString()
}
